<template>
  <h2 class="container my-4">
    {{$t("password_resets.title")}}
  </h2>

  <progress-nav :list="phaseList"
                ref="progressNav"
                class="container my-4"
                breakpoint="sm"
                :isLocked="(passwordReset.createPhase < 1 &&insertSuccessed !== null) || (passwordReset.createPhase >= 1 &&updateSuccessed !== null)"
                v-on:move="shiftPasswordResetCreatePhase"
                :borderPhase="borderPhase"/>

  <section v-if="summaryErrors.length"
           class="container my-4">
    <validation-errors class="alert alert-danger pb-0 overflow-hidden"
                       :errors="summaryErrors" />
  </section>

  <section v-show="passwordReset.createPhase === 0 && insertSuccessed === null">
    <section class="container my-4">
      <p class="text-pre-wrap">{{$t("password_resets.description_identification")}}</p>
    </section>
    <section class="container my-4">
      <basic-input v-model="passwordReset.loginCode"
                   type="text"
                   :inputClass="validationClass('LoginCode')"
                   :countVisible="false"
                   :required="true"
                   :attributes="modelAttributes.LoginCode" />
    </section>
    <section class="container my-4">
      <basic-input v-model="passwordReset.email"
                   type="email"
                   :inputClass="validationClass('Email')"
                   :countVisible="false"
                   :required="true"
                   :attributes="modelAttributes.Email" />
    </section>
  </section>
  <section v-show="passwordReset.createPhase === 0 && insertSuccessed === true">
    <section class="container my-4">
      <p class="text-pre-wrap" v-if="passwordResetSettings.timeType === 'Minute'">
        {{$t("password_resets.notify_url_send_minute", {time: passwordResetSettings.termOfValidity})
          + "\r\n" + $t("messages.contact_admin_about_email")}}
      </p>
      <p class="text-pre-wrap" v-else-if="passwordResetSettings.timeType === 'Hour'">
        {{$t("password_resets.notify_url_send_hour", {time: passwordResetSettings.termOfValidity})
          + "\r\n" + $t("messages.contact_admin_about_email")}}
      </p>
      <p class="text-pre-wrap" v-else-if="passwordResetSettings.timeType === 'Day'">
        {{$t("password_resets.notify_url_send_day", {time: passwordResetSettings.termOfValidity})
          + "\r\n" + $t("messages.contact_admin_about_email")}}
      </p>
    </section>
  </section>
  <section v-show="passwordReset.createPhase === 1 && insertSuccessed === true && updateSuccessed === null">
    <section class="container my-4">
      <p>{{$t("password_resets.description_new_password")}}</p>
    </section>
    <section class="container my-4">
      <basic-input v-model="passwordReset.password"
                   type="password"
                   :inputClass="validationClass('Password')"
                   :countVisible="true"
                   :required="true"
                   :attributes="modelAttributes.Password" />
      <password-policy-modal ref="passwordPolicyModal" />
    </section>
    <section class="container my-4">
      <basic-input v-model="passwordReset.passwordConfirm"
                   type="password"
                   :inputClass="validationClass('PasswordConfirm')"
                   :countVisible="true"
                   :required="true"
                   :attributes="modelAttributes.PasswordConfirm" />
    </section>
  </section>
  <section v-show="passwordReset.createPhase === 2 && updateSuccessed === true">
    <section class="container my-4">
      <p class="text-pre-wrap">{{$t("password_resets.reset_completed")}}</p>
    </section>
  </section>
  <section class="container my-4 d-flex">
    <button type="button"
            class="btn btn-outline-success me-auto"
            v-on:click.prevent="onPrevClick">
      {{returnButtonString}}
    </button>
    <button type="button"
            class="btn btn-outline-primary ms-2"
            v-on:click.prevent="onNextClick"
            v-if="(passwordReset.createPhase < 1 && insertSuccessed === null) || (passwordReset.createPhase >= 1 && updateSuccessed === null)">
      {{nextButtonString}}
    </button>
  </section>
</template>

<script>
  import { defineComponent } from "vue";
  import { xhrRequest, xhrErrors, xhrSummaryErrors } from "utils/xhr";
  import Spinner from "utils/spinner";

  import ValidationErrors from "components/base/validation_errors";

  import ProgressNav from "components/nav/progress_nav";

  import { getParameter } from "utils/url";

  import PasswordPolicyModal from "components/modal/password_policy_modal";
  import BasicInput from "components/input/basic_input";

  const computed = {
    phaseList() {
      return [
        this.$t("password_resets.create_phase_identification"),
        this.$t("password_resets.create_phase_new_password"),
        this.$t("password_resets.create_phase_finish"),
      ]
    },
    nextButtonString() {
      if (this.passwordReset.createPhase === 0) {
        return this.$t("buttons.sending");
      }
      return this.$t("buttons.update");
    },
    returnButtonString() {
      return this.$t("buttons.return");
    },
  };

  const methods = {
    loadPasswordReset(callback) {
      if (this.loading) {
        return;
      }
      this.loading = true;
      let spinner = new Spinner();
      xhrRequest(
        "GET",
        `passwordResets`,
        { ResetId: this.passwordReset.resetId },
        {
          success: data => {
            if (data.resetStatus !== "identification" || data.isUrlExpired) {
              this.passwordReset.isUrlExpired = data.isUrlExpired;
              window.location.href = "/UrlExpired";
            } else {
              this.id = data.id;
              this.insertSuccessed = true;
              this.borderPhase = 0;
              callback();
              this.errors = {};
              this.summaryErrors = {};
            }
          },
          error: (xhr, status, error) => {
            this.errors = xhrErrors(xhr);
            this.summaryErrors = xhrSummaryErrors(xhr);
            this.insertSuccessed = false;
          },
          complete: () => {
            spinner.stop();
            this.loading = false;
            if (!this.passwordReset.isUrlExpired && this.insertSuccessed === true) {
              callback();
            }
          }
        }
      );
    },
    onPrevClick() {
      window.location.href = "/Login";
    },
    onNextClick() {
      if (this.passwordReset.createPhase === 0) {
        this.validatePasswordReset(this.addPasswordReset);
        return;
      }
      if (this.passwordReset.createPhase === 1) {
        this.loadPasswordReset(this.validatePasswordReset.bind(this, this.editPasswordReset));
        return;
      }
    },
    movePhase(phase) {
      this.$refs.progressNav.movePhase(phase);
    },
    moveNextPhase() {
      this.$refs.progressNav.moveNextPhase();
    },
    shiftPasswordResetCreatePhase(data) {
      this.passwordReset.createPhase = data.phase;
    },
    validatePasswordReset(callback) {
      if (this.loading) {
        return;
      }
      this.loading = true;
      const spinner = new Spinner();
      xhrRequest(
        "POST",
        "passwordResets/validate",
        this.passwordReset,
        {
          success: data => {
            this.errors = {};
            this.summaryErrors = {};
            callback();
          },
          error: (xhr, status, error) => {
            this.errors = xhrErrors(xhr);
            this.summaryErrors = xhrSummaryErrors(xhr);
          },
          complete: () => {
            spinner.stop();
            this.loading = false;
          }
        }
      );
    },
    addPasswordReset() {
      if (this.id) {
        return;
      }
      this.loading = true;
      const spinner = new Spinner();
      xhrRequest(
        "POST",
        "passwordResets",
        this.passwordReset,
        {
          success: data => {
            this.insertSuccessed = true;
          },
          error: () => {
            this.insertSuccessed = false;
          },
          complete: () => {
            spinner.stop();
            this.loading = false;
          }
        }
      );
    },
    editPasswordReset() {
      this.loading = true;
      const spinner = new Spinner();
      xhrRequest(
        "PUT",
        `passwordResets/${this.id}`,
        this.passwordReset,
        {
          success: data => {
            this.updateSuccessed = true;
            this.moveNextPhase();
          },
          error: () => {
            this.updateSuccessed = false;
          },
          complete: () => {
            spinner.stop();
            this.loading = false;
          }
        }
      );
    },
    validationClass(key) {
      return this.errors[key] ? "is-invalid" : null;
    },
    onPasswordPolicyClick() {
      this.$refs.passwordPolicyModal.openPasswordPolicyModal();
    },
  };

  const onInit =  () => {
  };

  const onMounted = function () {
    xhrRequest(
      "GET",
      "passwordResets/setting",
      null,
      {
        success: data => {
          this.passwordResetSettings = data;         
          
        },
        error: () => {
        },
        complete: () => {
        }
      }
    );
    
    xhrRequest(
      "GET",
      "viewmodels/Anonymous/PasswordResetViewModel",
      null,
      {
        success: data => {
          this.modelAttributes = data;
        },
        error: () => {
        },
        complete: () => {
        }
      }
    );

    this.passwordReset.resetId = getParameter("resetId");
    if (this.passwordReset.resetId !== null) {
      this.loadPasswordReset(this.movePhase.bind(this, 1));
    }
  };

  export default defineComponent({
    template: "#container",
    data: () => {
      return {
        loading: false,
        id: null,
        passwordReset: {
          resetId: null,
          loginCode: "",
          email: "",
          password: "",
          passwordConfirm: "",
          resetStatus: "",
          isUrlExpired: false,
          createPhase: 0,
        },
        modelAttributes: {},
        passwordResetSettings: {},
        insertSuccessed: null,
        updateSuccessed: null,
        errors: {},
        summaryErrors: {},
        borderPhase: null,
      }
    },
    created: onInit,
    mounted: onMounted,
    methods: methods,
    computed: computed,
    components: {
      "progress-nav": ProgressNav,
      "validation-errors": ValidationErrors,
      "password-policy-modal": PasswordPolicyModal,
      "basic-input": BasicInput,
    },
  });
</script>
